import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Section wrapper with a labelled `<h2>` heading. Extracted from
 * StartRoute (`WorldsSection` + `LatestStoriesList` both rendered the
 * identical `<section>`+`<h2>` shape inline) when AccountRoute (Batch
 * 9A) added 2 more callsites — total 4 surfaces consume this shape,
 * passing the FB-5 ≥2 consumer rule.
 *
 * Heading classes (`text-sm font-semibold leading-[14px] text-dream-navy`)
 * are copied verbatim from the StartRoute h2 instances at lines 192-196
 * + 256-260 to preserve Sönke design-system fidelity per Batch 9A
 * Verification 2.
 *
 * Tone variant adds the muted secondary treatment for the
 * "Rechtliches" section on AccountRoute (mockup lines 52-55 — secondary
 * text color on the heading + child rows).
 */

const sectionHeadingVariants = cva('text-sm font-semibold leading-[14px]', {
  variants: {
    tone: {
      primary: 'text-dream-navy',
      secondary: 'text-dream-navy/60',
    },
  },
  defaultVariants: { tone: 'primary' },
});

type Tone = NonNullable<VariantProps<typeof sectionHeadingVariants>['tone']>;

export interface SectionHeadingProps {
  heading: string;
  /**
   * DOM id for `aria-labelledby` wiring. Auto-generated when omitted
   * (kebab-cased heading + suffix). Pass an explicit id when the
   * route's URL includes anchor links to the section.
   */
  id?: string;
  tone?: Tone;
  className?: string;
  children?: React.ReactNode;
}

let autoIdCounter = 0;

export function SectionHeading({
  heading,
  id,
  tone = 'primary',
  className,
  children,
}: SectionHeadingProps) {
  const headingId = id ?? `section-heading-${++autoIdCounter}`;
  return (
    <section
      className={cn('flex w-full flex-col items-start gap-3', className)}
      aria-labelledby={headingId}
    >
      <h2 id={headingId} className={sectionHeadingVariants({ tone })}>
        {heading}
      </h2>
      {children}
    </section>
  );
}
